import { createSelector } from "@reduxjs/toolkit";

const EMPTY_SOUS_PROFILES_STATE = {
  items: [],
  byId: {},
  current: null,
  loading: false,
  error: null,
  actingSession: {
    mode: "main",
    accessToken: null,
    actingAs: null,
    profile: null,
  },
};

const pickId = (value) => {
  if (!value) return null;

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || null;
  }

  if (typeof value !== "object") return null;

  const uuid = String(value.uuid || value.userId || value.id || value._id || "").trim();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid)) {
    return uuid;
  }

  return (
    pickId(value.id) ||
    pickId(value._id) ||
    pickId(value.userId) ||
    pickId(value.sub) ||
    pickId(value.pgId) ||
    pickId(value.uuid) ||
    pickId(value.spId) ||
    pickId(value.subprofileId) ||
    pickId(value.subProfileId) ||
    pickId(value.profileId)
  );
};

const findStoredUserById = (state, id) => {
  if (!state || !id) return null;
  const key = String(id);
  const users = state.user?.users || {};
  const usersById = state.user?.usersById || {};

  return (
    users[id] ||
    users[key] ||
    usersById[id] ||
    usersById[key] ||
    Object.values(users).find((user) => String(pickId(user)) === key) ||
    Object.values(usersById).find((user) => String(pickId(user)) === key) ||
    null
  );
};

export const selectSousProfilesState = (state) =>
  state.sousProfiles || EMPTY_SOUS_PROFILES_STATE;
export const selectSousProfiles = (state) => selectSousProfilesState(state).items;
export const selectSousProfilesById = (state) => selectSousProfilesState(state).byId;
export const selectCurrentSousProfile = (state) => selectSousProfilesState(state).current;
export const selectSousProfilesLoading = (state) => selectSousProfilesState(state).loading;
export const selectSousProfilesError = (state) => selectSousProfilesState(state).error;
export const selectVerifyTokenStatus = (state) => selectSousProfilesState(state).verifyTokenStatus;
export const selectVerifyTokenError = (state) => selectSousProfilesState(state).verifyTokenError;
export const selectSetPasswordStatus = (state) => selectSousProfilesState(state).setPasswordStatus;
export const selectSetPasswordError = (state) => selectSousProfilesState(state).setPasswordError;

export const selectActingSession = (state) => selectSousProfilesState(state).actingSession;
export const selectIsActingAsSubprofile = (state) =>
  selectSousProfilesState(state).actingSession?.mode === "subprofile";

export const selectPrincipalUser = (state) => {
  const authUser = state.auth?.user || state.auth?.currentUser || state.auth?.me || null;
  const authUserId = pickId(authUser);
  const storedUser = findStoredUserById(state, authUserId);

  if (!storedUser) return authUser;

  const id = pickId(storedUser) || authUserId;
  return {
    ...(authUser || {}),
    ...storedUser,
    ...(id ? { id } : {}),
  };
};

export const selectPrincipalUserId = (state) => pickId(selectPrincipalUser(state));

export const selectActiveSubprofileId = (state) => {
  const session = selectActingSession(state);
  if (session?.mode !== "subprofile") return null;

  return (
    pickId(session.actingAs) ||
    pickId(session.profile) ||
    pickId(session.subprofile) ||
    pickId(session.subProfile)
  );
};

export const selectEffectiveUserId = (state) =>
  selectActiveSubprofileId(state) || selectPrincipalUserId(state);

export const selectEffectiveUser = (state) => {
  if (!selectIsActingAsSubprofile(state)) return selectPrincipalUser(state);

  const userId = selectEffectiveUserId(state);
  const sessionProfile = selectActingSession(state)?.profile || null;
  const storedUser = findStoredUserById(state, userId);
  const cachedSubprofile =
    state.sousProfiles?.byId?.[userId] ||
    state.sousProfiles?.items?.find?.(
      (profile) => String(pickId(profile)) === String(userId)
    ) ||
    null;

  if (storedUser) {
    return {
      ...sessionProfile,
      ...cachedSubprofile,
      ...storedUser,
      id: pickId(storedUser) || userId,
      subprofileId:
        storedUser.subprofileId ||
        storedUser.subProfileId ||
        cachedSubprofile?.subprofileId ||
        cachedSubprofile?.subProfileId ||
        userId,
      isSubprofile: true,
    };
  }

  if (cachedSubprofile && userId) {
    return {
      ...sessionProfile,
      ...cachedSubprofile,
      id: pickId(cachedSubprofile) || userId,
      subprofileId:
        cachedSubprofile.subprofileId || cachedSubprofile.subProfileId || userId,
      isSubprofile: true,
    };
  }

  if (sessionProfile && userId) {
    return {
      ...sessionProfile,
      id: pickId(sessionProfile) || userId,
      subprofileId:
        sessionProfile.subprofileId || sessionProfile.subProfileId || userId,
      isSubprofile: true,
    };
  }

  return userId ? { id: userId, subprofileId: userId, isSubprofile: true } : null;
};

export const selectEffectiveAccessToken = (state) => {
  const session = selectActingSession(state);
  if (session?.mode !== "subprofile") return null;
  return session.accessToken || null;
};

export const selectEffectiveUserContext = createSelector(
  [
    selectPrincipalUser,
    selectPrincipalUserId,
    selectEffectiveUser,
    selectEffectiveUserId,
    selectActiveSubprofileId,
    selectActingSession,
    selectIsActingAsSubprofile,
    selectEffectiveAccessToken,
  ],
  (
    principalUser,
    principalUserId,
    effectiveUser,
    effectiveUserId,
    activeSubprofileId,
    actingSession,
    isActingAsSubprofile,
    effectiveAccessToken
  ) => ({
    principalUser,
    principalUserId,
    effectiveUser,
    effectiveUserId,
    activeSubprofileId,
    actingSession,
    isActingAsSubprofile,
    isSubprofileContext: isActingAsSubprofile,
    effectiveAccessToken,
  })
);
